import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Cookie, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const KEY = "xortec_cookie_consent_v1";

export default function CookieConsent() {
  const [show, setShow] = useState(false);
  const [details, setDetails] = useState(false);

  useEffect(() => {
    try {
      const existing = localStorage.getItem(KEY);
      if (!existing) setShow(true);
    } catch {
      // if localStorage unavailable, don't show – nothing to persist anyway
    }
  }, []);

  const save = (value) => {
    try {
      localStorage.setItem(
        KEY,
        JSON.stringify({ level: value, ts: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="cookie-consent"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[70]"
        >
          <div className="rounded-2xl border border-primary/40 bg-card/95 backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,0.55)] p-5 relative">
            <button
              type="button"
              aria-label="Schließen"
              onClick={() => save("necessary")}
              className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-base leading-tight mb-1">Cookies &amp; Datenschutz</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Wir verwenden ausschließlich <strong>technisch notwendige</strong> Speicher (localStorage),
                  um Ihre gespeicherten Projekte lokal in Ihrem Browser zu halten.
                  Sollten künftig Analyse- oder Marketing-Cookies eingesetzt werden, kommen diese nur mit Ihrer
                  Zustimmung zum Einsatz.
                </p>
                {details && (
                  <div className="mt-3 rounded-md border border-border bg-secondary/40 p-3 text-xs text-muted-foreground space-y-2">
                    <div>
                      <span className="font-semibold text-foreground">Technisch notwendig:</span>{" "}
                      Speicherung Ihrer Projekte, Cookie-Entscheidung, Sprachauswahl.
                    </div>
                    <div>
                      <span className="font-semibold text-foreground">Analyse / Marketing:</span>{" "}
                      derzeit nicht aktiv. Falls aktiviert, dienen sie der anonymen Nutzungsauswertung.
                    </div>
                    <div>
                      Details siehe{" "}
                      <a
                        href="https://xortec.de/Rechtliches/Datenschutz/"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary hover:underline"
                      >
                        Datenschutzerklärung
                      </a>
                      .
                    </div>
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    data-testid="cookie-accept-all"
                    onClick={() => save("all")}
                    className="bg-primary text-primary-foreground hover:opacity-90"
                  >
                    Alle akzeptieren
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="cookie-necessary-only"
                    onClick={() => save("necessary")}
                  >
                    Nur technisch notwendig
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDetails((d) => !d)}
                    className="text-muted-foreground"
                    data-testid="cookie-details"
                  >
                    {details ? "Weniger anzeigen" : "Details"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
